// imports
import { useEffect, useState, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import type { FilterSlot } from '@/shared/types/inputArea'
import type { AssistantMessageConfig } from '@/ui/components/ChatMessageTypes/Assistant/types/AssistantMessageTypes'
import type { RootState } from '@/utils/store/store'
import type { MetadataLine } from '@/ui/advancedAssistants/MMA/types/MMA'

// components
import InputArea from '@/ui/components/InputArea/InputArea'
import AnswerArea from '@/ui/components/AnswerArea/AnswerArea'

// utils
import { useSpace } from '@/hooks/useSpace'
import { useConfig } from '@/hooks/useConfig'
import { useAuthentication } from '@/hooks/useAuthentication'
import { useNavigation } from '@/hooks/useNavigation'
import { useMMAState } from '@/ui/advancedAssistants/MMA/useMMAState'
import { useMMAHandlers } from '@/ui/advancedAssistants/MMA/useMMAHandlers'
import storage from '@/utils/storage'

// styles
import './Chat.scss'

type SendPayload = {
    message: string
    file_urls?: string[]
    preview_urls?: string[]
}

type PersistedChatData = {
    message?: string
    audio?: Blob
}

export type ChatProps = {
    assistantConfig?: AssistantMessageConfig
    advancedSendRequest: (payload: SendPayload) => void | Promise<void>
    simplified?: boolean
    persistedData?: PersistedChatData
    onRetry?: () => void
    onShareToDev?: (errorPayload?: Record<string, unknown>) => void
    filterSlot?: FilterSlot
    showQuickLines?: boolean
}

const Chat = ({
    assistantConfig,
    advancedSendRequest,
    simplified,
    persistedData,
    onRetry,
    filterSlot,
    showQuickLines = true,
}: ChatProps) => {

    const [newQuestion, setNewQuestion] = useState('')

    const { lastUpdate, chatWaitsForResponse } = useSelector((state: RootState) => state.assistants)
    // read straight from the store (not copied in an effect), so the status of a new answer shows in its first render
    const waitingForResponse = chatWaitsForResponse

    const { navigateTo } = useNavigation()
    const { information, validateTokenAndRehydrate } = useAuthentication()
    const { t } = useTranslation()
    const { getActiveAssistant, getActiveSpace } = useSpace()

    const assistant = getActiveAssistant()
    const space = getActiveSpace()

    const { isDarkTheme } = useConfig()
    
    // Get MMA state for documentation line requirement check
    const mmaState = useMMAState()
    const { currentFilters } = mmaState
    const { handlers } = useMMAHandlers(mmaState)

    const currentFiltersRef = useRef(currentFilters)
    useEffect(() => {
        currentFiltersRef.current = currentFilters
    }, [currentFilters])

    // Get available lines from assistant metadata
    const lineElements: MetadataLine | undefined = assistant?.metadata?.filter?.line?.elements
    const availableLines = useMemo(() => {
        if (!lineElements || Object.keys(lineElements).length === 0) return []
        return Object.entries(lineElements).map(([key, val]) => ({
            value: key,
            label: val?.displayName ?? key,
        }))
    }, [lineElements])

    // The preferences of the user can arrive after the filters were initialised (first visit on this device, MMA.tsx
    // loads them): the default line is selected then, unless a line is selected already. This state is the one of the
    // pills, MMA.tsx has its own.
    const handlersRef = useRef(handlers)
    handlersRef.current = handlers
    const assistantRef = useRef(assistant)
    assistantRef.current = assistant
    const spaceIdRef = useRef(space?.id)
    spaceIdRef.current = space?.id

    useEffect(() => {
        const selectDefaultLine = (): void => {
            const spaceId = spaceIdRef.current
            const current = currentFiltersRef.current
            if (!spaceId || current?.line?.length) return

            const defaultLine = storage.getDefaultLine(spaceId)
            const element = defaultLine ? assistantRef.current?.metadata?.filter?.line?.elements?.[defaultLine] : undefined
            if (!defaultLine || !element) return

            handlersRef.current.saveSelection(
                { line: [{ value: defaultLine, label: element.displayName || defaultLine }], source: current?.source || [] },
                false,
                undefined
            )
        }

        window.addEventListener('mma:preferencesLoaded', selectDefaultLine)
        return () => window.removeEventListener('mma:preferencesLoaded', selectDefaultLine)
    }, [])

    const handleToggleLine = (line: { value: string; label: string }): void => {
        const currentLineValues = currentFiltersRef.current?.line?.map((l) => l.value) || []
        const isSelected = currentLineValues.includes(line.value)

        const isSingleSelect = assistant?.config?.id?.endsWith('documentation') ||
            assistant?.config?.id?.endsWith('-opra') ||
            assistant?.config?.id?.endsWith('ddlr')

        let updatedLines
        if (isSingleSelect) {
            updatedLines = isSelected ? [] : [line]
        } else {
            updatedLines = isSelected
                ? currentFiltersRef.current?.line?.filter((l) => l.value !== line.value) || []
                : [...(currentFiltersRef.current?.line || []), line]
        }

        handlers.saveSelection(
            { line: updatedLines, source: currentFiltersRef.current?.source || [] },
            false,
            undefined
        )
    }

    // Derive messageList synchronously from lastUpdate so AnswerArea gets the
    // updated list in the same render that Redux triggered — no extra setState cycle.
    const messageList = useMemo(
        () => assistant?.activeChat?.messageList,
        // lastUpdate only triggers the read: the assistant is mutated in place, its identity does not change
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [lastUpdate, assistant]
    )

    useEffect(() => {
        if (persistedData?.message)
            setNewQuestion(persistedData.message)
    }, [persistedData?.message])

    const sendRequest = async () => {
        advancedSendRequest({ message: newQuestion })
        setNewQuestion('')
        if (simplified)
            navigateTo(`/${space?.id}/${assistant?.config.id}`)
    }

    const sendRequestWithAttachment = ({ message, file_urls, preview_urls }: SendPayload) => {
        advancedSendRequest({ message, file_urls, preview_urls })
        setNewQuestion('')
        if (simplified)
            navigateTo(`/${space?.id}/${assistant?.config.id}`)
    }

    // For documentation/opra/ddlr assistants, require a line to be selected
    const isSingleSelect = assistant?.config?.id?.endsWith('documentation') ||
            assistant?.config?.id?.endsWith('-opra') ||
            assistant?.config?.id?.endsWith('ddlr')
    const hasLineSelected = currentFilters?.line && currentFilters.line.length > 0
    const isInputDisabled = isSingleSelect && !hasLineSelected
    const inputDisabledMessage = isInputDisabled ? 'Please select a production line' : t('chat:chatDisabled')

    return (
        <div className="chat-root">
            {!simplified &&
                <AnswerArea
                    chatId={assistant?.activeChat?.chatId}
                    messageList={messageList || []}
                    features={assistantConfig?.features?.output}
                    waitingForResponse={waitingForResponse}
                    isDarkTheme={isDarkTheme}
                    activeAssistantConfig={assistantConfig}
                    userImage={information?.profilePhoto ?? undefined}
                    onRetry={onRetry}
                    currentFilters={currentFilters}
                    availableLines={showQuickLines ? availableLines : undefined}
                    onToggleLine={showQuickLines ? handleToggleLine : undefined}
                />
            }
            <InputArea
                text={newQuestion}
                updateText={setNewQuestion}
                sendRequest={sendRequest}
                onSendPayload={sendRequestWithAttachment}
                assistant={assistant}
                waitingForResponse={waitingForResponse}
                disabled={isInputDisabled}
                disabledMessage={inputDisabledMessage}
                validateTokenAndRehydrate={validateTokenAndRehydrate}
                persistedData={persistedData}
                filterSlot={filterSlot}
            />
        </div>
    )
}

export default Chat
